import {
  doc, getDoc, setDoc, getDocs, collection,
  runTransaction, serverTimestamp,
  type DocumentReference, type Transaction,
} from 'firebase/firestore';
import { db } from '../firebase';
import { todayStr, yesterdayStr } from '../../utils/helpers';
import type {
  DeckProgress,
  DeckProgressMap,
  ClassSnapshot,
  ClassSnapshotMap,
  ProgressMetaDoc,
  ProgressMeta,
  StreakData,
} from '../../types/progress';

// ─── Refs ───────────────────────────────────────────

function metaRef(uid: string): DocumentReference {
  return doc(db, 'progress', uid);
}

// ─── Runtime sanitizer for Firestore ProgressMetaDoc ──

function sanitizeMetaDoc(raw: Record<string, unknown>): ProgressMetaDoc {
  const snapshots = raw.classSnapshots;
  return {
    streak: typeof raw.streak === 'number' ? raw.streak : 0,
    lastDay: typeof raw.lastDay === 'string' ? raw.lastDay : null,
    lastSeen: typeof raw.lastSeen === 'string' ? raw.lastSeen : null,
    classSnapshots: (snapshots && typeof snapshots === 'object' && !Array.isArray(snapshots))
      ? snapshots as ClassSnapshotMap
      : {},
    lastTamkeenSessionDay: typeof raw.lastTamkeenSessionDay === 'string'
      ? raw.lastTamkeenSessionDay
      : null,
  };
}

function deckRef(uid: string, deckId: string): DocumentReference {
  return doc(db, 'progress', uid, 'decks', deckId);
}

// ═══════════════════════════════════════════════════════
// Deck Progress
//
// Document shape: { seen, total }
//   seen  = unique cards attempted (incremented once per card lifetime)
//   total = snapshot of total cards in deck
// ═══════════════════════════════════════════════════════

export async function getDeckProgress(uid: string, deckId: string): Promise<DeckProgress | null> {
  const snap = await getDoc(deckRef(uid, deckId));
  return snap.exists() ? (snap.data() as DeckProgress) : null;
}

export async function getAllDeckProgress(uid: string): Promise<DeckProgressMap> {
  const snap = await getDocs(collection(db, 'progress', uid, 'decks'));
  const result: DeckProgressMap = {};
  snap.docs.forEach((d) => { result[d.id] = d.data() as DeckProgress; });
  return result;
}

// ═══════════════════════════════════════════════════════
// Class Snapshots (synced across devices)
//
// Stored in progress/{uid} → classSnapshots: { [classId]: { ... } }
// ═══════════════════════════════════════════════════════

export async function saveClassSnapshot(uid: string, classId: string, data: ClassSnapshot): Promise<void> {
  const ref = metaRef(uid);
  await setDoc(ref, { classSnapshots: { [classId]: data } }, { merge: true });
}

export async function getClassSnapshots(uid: string): Promise<ClassSnapshotMap> {
  const snap = await getDoc(metaRef(uid));
  if (!snap.exists()) return {};
  return sanitizeMetaDoc(snap.data()).classSnapshots || {};
}

// ═══════════════════════════════════════════════════════
// Daily Tamkeen Session Flag (synced across devices)
//
// Stored in progress/{uid} → lastTamkeenSessionDay: "YYYY-MM-DD"
// ═══════════════════════════════════════════════════════

export async function markTamkeenSessionDone(uid: string): Promise<void> {
  const ref = metaRef(uid);
  const today = todayStr();
  await setDoc(ref, { lastTamkeenSessionDay: today }, { merge: true });
}

export async function getTamkeenSessionDay(uid: string): Promise<string | null> {
  const snap = await getDoc(metaRef(uid));
  if (!snap.exists()) return null;
  return sanitizeMetaDoc(snap.data()).lastTamkeenSessionDay || null;
}

/**
 * Single read of the progress meta document.
 * Replaces separate getStreak + getClassSnapshots + getTamkeenSessionDay calls.
 */
export async function getProgressMeta(uid: string): Promise<ProgressMeta> {
  const snap = await getDoc(metaRef(uid));
  if (!snap.exists()) {
    return { streak: 0, lastDay: null, lastSeen: null, classSnapshots: {}, lastTamkeenSessionDay: null };
  }
  const data = sanitizeMetaDoc(snap.data());
  const today = todayStr();
  const yesterday = yesterdayStr();
  const streak = (data.lastDay === today || data.lastDay === yesterday) ? (data.streak || 0) : 0;
  return {
    streak,
    lastDay: data.lastDay || null,
    lastSeen: data.lastSeen || null,
    classSnapshots: data.classSnapshots || {},
    lastTamkeenSessionDay: data.lastTamkeenSessionDay || null,
  };
}

// ═══════════════════════════════════════════════════════
// Streak (Daily)
//
// Document shape at progress/{uid}: { streak, lastDay }
//   streak  = current consecutive active days
//   lastDay = "YYYY-MM-DD" of last activity
// ═══════════════════════════════════════════════════════

export async function getStreak(uid: string): Promise<StreakData> {
  const snap = await getDoc(metaRef(uid));
  if (!snap.exists()) return { streak: 0, lastDay: null };
  const data = sanitizeMetaDoc(snap.data());
  const today = todayStr();
  const yesterday = yesterdayStr();
  if (data.lastDay !== today && data.lastDay !== yesterday) {
    return { streak: 0, lastDay: data.lastDay || null };
  }
  return { streak: data.streak || 0, lastDay: data.lastDay || null };
}

export async function updateStreak(uid: string): Promise<StreakData> {
  const ref = metaRef(uid);
  const today = todayStr();

  return runTransaction(db, async (tx: Transaction) => {
    const snap = await tx.get(ref);

    if (!snap.exists()) {
      const init: StreakData = { streak: 1, lastDay: today };
      tx.set(ref, init);
      return init;
    }

    const data = sanitizeMetaDoc(snap.data());
    if (data.lastDay === today) return { streak: data.streak || 0, lastDay: data.lastDay } as StreakData;

    const newStreak = data.lastDay === yesterdayStr() ? (data.streak || 0) + 1 : 1;
    const updated: StreakData = { streak: newStreak, lastDay: today };
    tx.set(ref, updated, { merge: true });
    return updated;
  });
}
